import { FC, createContext, useContext, useMemo, useEffect, useState, ReactNode } from 'react';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { FacetSearchPayload } from './types';

// turn a facetFilter into a string following rules specified in
// https://1stdibs.atlassian.net/browse/CAM-583
function reduceFacetFilterToString(facetFilter: FacetSearchPayload): string | null {
    const { style, project, room, creator } = facetFilter;
    const labels = [];
    if (creator.length > 0) {
        return null;
    }
    for (const facet of [style, project, room]) {
        if (facet.length > 1) {
            return null;
        } else if (facet[0]) {
            labels.push(facet[0].label);
        }
    }
    if (labels.length < 1 || labels.length > 2) {
        return null;
    }
    return labels.join(' ');
}

const messages = defineMessages({
    faceted: {
        id: 'abt.PhotosSearchBrowseWrapper.facetHeader',
        defaultMessage: `{attribute} Design Ideas`,
    },
    default: {
        id: 'abt.PhotosSearchBrowseWrapper.facetHeaderDefault',
        defaultMessage: `Home Design Ideas`,
    },
});
const HeadlineContext = createContext('');

type ProviderProps = {
    facetFilter: FacetSearchPayload;
    children: ReactNode;
};

export const HeadlineContextProvider: FC<ProviderProps> = ({ facetFilter, children }) => {
    const intl = useIntl();

    const memoizedHeadline = useMemo(() => {
        const filterString = reduceFacetFilterToString(facetFilter);
        return filterString
            ? intl.formatMessage(messages.faceted, { attribute: filterString })
            : intl.formatMessage(messages.default);
    }, [facetFilter, intl]);

    const [headline, setHeadline] = useState(memoizedHeadline);

    useEffect(() => {
        setHeadline(memoizedHeadline);
    }, [memoizedHeadline]);
    return <HeadlineContext.Provider value={headline}>{children}</HeadlineContext.Provider>;
};

export const useHeadlineContext = (): string => useContext(HeadlineContext);
